import { addBreadcrumb } from '@sentry/angular';
import { getParentPath } from '../guard/utils';
import { DAMO_AUTH_PATHS } from '../../auth-paths';
import { Component, inject, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FirebaseError } from 'firebase/app';
import { getMultiFactorResolver, TotpMultiFactorGenerator, type MultiFactorError, type MultiFactorResolver } from 'firebase/auth';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { I18N_DEFAULT_LANG, I18N_ENGLISH_LANG, I18N_FRENCH_LANG } from '../../../i18n';
import { AuthService } from '../services/auth.service';
import { AuthError } from '../errors/auth.error';
import { SignInCardComponent } from './sign-in-card/sign-in-card.component';
import { InfoCardComponent } from './info-card/info-card.component';

@Component({
  selector: 'app-sign-in',
  imports: [
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    TranslatePipe,
    SignInCardComponent,
    InfoCardComponent,
  ],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
})
export class SignInComponent {
  protected readonly I18N_ENGLISH_LANG = I18N_ENGLISH_LANG;
  protected readonly DAMO_AUTH_PATHS = DAMO_AUTH_PATHS;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly auth = inject(Auth);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly loading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly mfaResolver = signal<MultiFactorResolver | null>(null);
  protected readonly currentLang = signal(this.translate.getCurrentLang() || this.translate.getFallbackLang() || I18N_DEFAULT_LANG);
  protected readonly mfaForm = this.formBuilder.nonNullable.group({
    code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
  });

  protected toggleLanguage(): void {
    const next = this.currentLang() === I18N_ENGLISH_LANG ? I18N_FRENCH_LANG : I18N_ENGLISH_LANG;
    this.translate.use(next);
    this.currentLang.set(next);
  }

  protected async signIn(email: string, password: string): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      await this.authService.signIn(email, password);
      if (this.route.snapshot.queryParams['mfaRevoked'] === 'true') {
        await this.authService.clearMfaEnabled().catch((): void => undefined);
      }
      await this.handlePostLogin();
      addBreadcrumb({ message: 'User signed in successfully', level: 'info' });
    } catch (err: unknown) {
      if ((err as { code?: string })?.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(this.auth, err as MultiFactorError);
        if (resolver.hints.some((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID)) {
          this.mfaResolver.set(resolver);
        } else {
          this.errorMessage.set(this.translate.instant('auth.mfa.unsupported'));
        }
      } else if (err instanceof AuthError) {
        this.errorMessage.set(this.translate.instant(err.message));
      } else {
        this.errorMessage.set(this.translate.instant('autherror.default'));
      }
    } finally {
      this.loading.set(false);
    }
  }

  protected async submitMfa(): Promise<void> {
    if (this.mfaForm.invalid || !this.mfaResolver()) {
      this.mfaForm.markAllAsTouched();
      return;
    }

    const resolver = this.mfaResolver();
    const hint = resolver.hints.find((factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!hint) {
      this.errorMessage.set(this.translate.instant('auth.mfa.unsupported'));
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, this.mfaForm.getRawValue().code));
      await this.handlePostLogin();
    } catch (err: unknown) {
      this.errorMessage.set(this.translate.instant(err instanceof FirebaseError && err.code === 'auth/invalid-verification-code' ? 'auth.mfa.invalid' : 'auth.mfa.error'));
    } finally {
      this.loading.set(false);
    }
  }

  protected cancelMfa(): void {
    this.mfaResolver.set(null);
    this.mfaForm.reset();
    this.errorMessage.set(null);
  }

  private async handlePostLogin(): Promise<void> {
    const { SAMLRequest, RelayState } = this.route.snapshot.queryParams;
    const queryParams = SAMLRequest ? { SAMLRequest, ...(RelayState ? { RelayState } : {}) } : undefined;
    await this.router.navigate([`${getParentPath(this.route.snapshot)}/${DAMO_AUTH_PATHS.REDIRECT}`], { queryParams });
  }
}
